import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useFx } from "../lib/fx";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Star, Verified, Bitcoin, MapPin, Users, Clock, ArrowLeft, Check, Lock, Eye } from "lucide-react";
import { formatServiceDuration } from "../lib/serviceMeta";

const CRYPTO_OPTIONS = ["USDT", "USDC", "BTC", "ETH"];

const rangesOverlap = (aStart, aEnd, bStart, bEnd) =>
  aStart && bStart && aStart <= (bEnd || bStart) && (aEnd || aStart) >= bStart;

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const { format, userCurrency } = useFx();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [availability, setAvailability] = useState(null);

  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    guests: 2,
    user_notes: "",
    payment_method: "crypto",
    crypto_currency: "USDT",
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setData(null); setError(null); setAvailability(null);
        const [{ data: svcData }, availRes] = await Promise.all([
          api.get(`/services/${id}`),
          api.get(`/services/${id}/availability`).catch(() => ({ data: { unavailable: [] } })),
        ]);
        setData(svcData);
        setAvailability(availRes.data);
      } catch (e) { setError(formatErr(e)); }
    })();
  }, [id]);

  const conflict = useMemo(() => {
    if (!availability || !form.start_date) return null;
    const ranges = availability.unavailable || [];
    const conflict = ranges.find((r) =>
      rangesOverlap(form.start_date, form.end_date, r.start_date, r.end_date),
    );
    return conflict || null;
  }, [availability, form.start_date, form.end_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login", { state: { redirect: `/services/${id}` } });
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const { data } = await api.post("/bookings", {
        service_id: id,
        ...form,
      });
      setBookingResult(data.booking);
    } catch (e) { setError(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  if (error && !data) return (
    <div className="pt-32 px-6 max-w-3xl mx-auto">
      <div className="text-rose-300">{error}</div>
      <Link to="/" className="btn btn-outline mt-4">{t("common.back", "Home")}</Link>
    </div>
  );
  if (!data) return <div className="pt-32 px-6 text-muted">{t("common.loading", "Loading…")}</div>;

  const { service: s, provider, destination, category, reviews } = data;
  const imgs = s.images?.length ? s.images : ["https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600"];
  const deposit = ((s.deposit_percent || 30) / 100) * (s.base_price || 0);
  const isPublished = s.status === "published";
  const previewBannerCopy = isPublished
    ? null
    : s.status === "archived"
      ? t("provider_dash.preview_banner_archived", "Preview only — this service is archived and isn't visible publicly.")
      : t("provider_dash.preview_banner_pending", "Preview only — this service is awaiting concierge review and isn't visible publicly yet.");

  return (
    <div className="pt-24 pb-24">
      <div className="px-5 md:px-10 mx-auto max-w-7xl">
        <Link to={destination ? `/destinations/${destination.slug}` : "/"}
              className="inline-flex items-center gap-2 text-muted hover:text-teal text-sm mb-6"
              data-testid="back-link">
          <ArrowLeft size={14}/> {t("service_detail.back_to_destination", "Back to {{name}}", { name: destination?.name || t("common.back", "home") })}
        </Link>

        {previewBannerCopy && (
          <div
            className="luxe-card p-4 md:px-5 md:py-3.5 mb-6 flex items-start gap-3 border-champagne/40 bg-champagne/5"
            data-testid="preview-banner"
          >
            <Eye size={16} className="text-champagne shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="text-champagne font-medium">{previewBannerCopy}</span>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="luxe-card aspect-[16/10] relative">
              <img src={imgs[activeImage]} alt={s.title} className="w-full h-full object-cover"/>
              <div className="absolute top-4 left-4 flex gap-2">
                {s.is_featured && <span className="chip info">{t("service_detail.featured", "Featured")}</span>}
                {s.crypto_accepted && <span className="chip"><Bitcoin size={11}/> {t("service_detail.crypto_accepted", "Crypto")}</span>}
                {s.verified && <span className="chip success"><Verified size={11}/> {t("service_detail.verified_by_concierge", "Verified")}</span>}
              </div>
            </div>
            {imgs.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-3">
                {imgs.map((u, i) => (
                  <button key={i} onClick={() => setActiveImage(i)}
                          className={`aspect-square rounded-xl overflow-hidden border ${i === activeImage ? "border-teal" : "border-white/10"}`}
                          data-testid={`thumb-${i}`}>
                    <img src={u} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-8">
              <div className="eyebrow">{category?.name}</div>
              <h1 className="section-title text-4xl md:text-5xl mt-2">{s.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted">
                {s.location && <span className="inline-flex items-center gap-1.5"><MapPin size={14}/> {s.location}</span>}
                {formatServiceDuration(s, t) && (
                  <span className="inline-flex items-center gap-1.5" data-testid="service-duration">
                    <Clock size={14}/> {formatServiceDuration(s, t)}
                  </span>
                )}
                {s.max_guests && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={14}/> {t("service_detail.max_guests_short", "Up to {{count}} guests", { count: s.max_guests })}
                  </span>
                )}
                {provider?.rating_avg ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Star size={14} className="fill-champagne text-champagne"/>
                    {t("provider_dash.rating_summary", "{{avg}} avg · {{count}} reviews", { avg: provider.rating_avg, count: provider.rating_count })}
                  </span>
                ) : null}
              </div>

              <p className="mt-6 text-base leading-relaxed text-muted">{s.description}</p>

              {!!s.included?.length && (
                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <div>
                    <div className="eyebrow mb-3">{t("service_detail.included", "Included")}</div>
                    <ul className="space-y-1.5 text-sm">
                      {s.included.map((x, i) => <li key={i} className="flex gap-2"><Check size={16} className="text-teal mt-0.5 shrink-0"/> {x}</li>)}
                    </ul>
                  </div>
                  {!!s.excluded?.length && (
                    <div>
                      <div className="eyebrow mb-3">{t("service_detail.excluded", "Not included")}</div>
                      <ul className="space-y-1.5 text-sm text-muted">
                        {s.excluded.map((x, i) => <li key={i}>· {x}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {s.cancellation_policy && (
                <div className="mt-8 luxe-card p-5">
                  <div className="eyebrow mb-2">{t("service_detail.cancellation", "Cancellation policy")}</div>
                  <p className="text-sm text-muted">{s.cancellation_policy}</p>
                </div>
              )}

              {provider && (
                <div className="mt-10 luxe-card p-6">
                  <div className="eyebrow mb-2">{t("service_detail.provider_heading", "Provider")}</div>
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-teal/15 grid place-items-center text-teal font-serif-display text-xl">{provider.name?.[0]}</div>
                    <div>
                      <div className="font-serif-display text-2xl text-bone">{provider.name}</div>
                      <div className="text-sm text-muted">{provider.description}</div>
                      <div className="mt-2 flex items-center gap-2 chip success">
                        <Verified size={12}/> {t("service_detail.provider_verified_label", "Verified")} · Meet &amp; Mint
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-10">
                <div className="eyebrow mb-4">{t("service_detail.reviews", "Reviews")}</div>
                {reviews?.length ? (
                  <div className="space-y-4">
                    {reviews.map((r) => (
                      <div key={r.id} className="luxe-card p-5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">{r.user_name || t("common.guests", "Guest")}</div>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star key={i} size={12} className={i < r.rating ? "fill-champagne text-champagne" : "text-muted/30"}/>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-muted">{r.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted">{t("service_detail.no_reviews", "No reviews yet — be the first.")}</p>
                )}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              {!bookingResult ? (
                <form onSubmit={submit} className="luxe-card p-6 space-y-4" data-testid="booking-form">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="label-tiny">{t("common.from", "From")}</div>
                      <div className="font-serif-display text-3xl">{format(s.base_price, s.currency)}</div>
                      {userCurrency !== s.currency && (
                        <div className="text-[11px] text-muted/70 font-mono">≈ {format(s.base_price, s.currency, { native: true })} native</div>
                      )}
                    </div>
                    <div className="chip">{s.currency}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="label-tiny">{t("service_detail.start_date", "Start")}</span>
                      <input type="date" required value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="input-bare mt-1" data-testid="booking-start"/>
                    </label>
                    <label className="block">
                      <span className="label-tiny">{t("service_detail.end_date", "End")}</span>
                      <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="input-bare mt-1" data-testid="booking-end"/>
                    </label>
                  </div>

                  <label className="block">
                    <span className="label-tiny">{t("service_detail.guests_label", "Guests")}</span>
                    <input type="number" min={1} max={s.max_guests || 50} value={form.guests} onChange={e => setForm({...form, guests: Number(e.target.value)})} className="input-bare mt-1" data-testid="booking-guests"/>
                  </label>

                  <div>
                    <span className="label-tiny">{t("service_detail.payment_method", "Payment")}</span>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button type="button" onClick={() => setForm({...form, payment_method: "crypto"})} className={`tab-pill ${form.payment_method === "crypto" ? "active" : ""}`} data-testid="pay-crypto">
                        {t("service_detail.crypto", "Crypto")}
                      </button>
                      <button type="button" onClick={() => setForm({...form, payment_method: "card"})} className={`tab-pill ${form.payment_method === "card" ? "active" : ""}`} data-testid="pay-card">
                        {t("service_detail.card", "Card")}
                      </button>
                    </div>
                  </div>

                  {form.payment_method === "crypto" && (
                    <label className="block">
                      <span className="label-tiny">{t("service_detail.stablecoin", "Stablecoin")}</span>
                      <select className="input-bare mt-1" value={form.crypto_currency} onChange={e => setForm({...form, crypto_currency: e.target.value})} data-testid="booking-crypto">
                        {CRYPTO_OPTIONS.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </label>
                  )}

                  <label className="block">
                    <span className="label-tiny" data-testid="booking-notes-label">{t("service_detail.notes_label", "Notes")}</span>
                    <textarea value={form.user_notes} onChange={e => setForm({...form, user_notes: e.target.value})}
                              placeholder={t("service_detail.notes_placeholder", "Anything we should know? Allergies, birthdays, preferences…")}
                              className="input-bare mt-1" data-testid="booking-notes"/>
                  </label>

                  <div className="flex items-baseline justify-between text-sm pt-2">
                    <span className="text-muted">{t("service_detail.protected_deposit_today", "Protected deposit due today")}</span>
                    <span className="font-serif-display text-xl">{format(deposit, s.currency)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted">
                    <Lock size={12}/> {t("service_detail.deposit_hint", "Funds reserved until you confirm completion.")}
                  </div>

                  {conflict && (
                    <div className="luxe-card p-3 text-sm border-rose-300/40 bg-rose-500/5 text-rose-200" data-testid="availability-conflict">
                      {t("service_calendar.booked_dates_warning", "These dates are unavailable. Please pick different ones.")}
                      <div className="text-[11px] text-rose-200/70 font-mono mt-1">
                        {conflict.start_date}{conflict.end_date && conflict.end_date !== conflict.start_date ? ` → ${conflict.end_date}` : ""}
                      </div>
                    </div>
                  )}

                  {error && <div className="text-rose-300 text-sm">{error}</div>}

                  <button type="submit" disabled={submitting || !isPublished || !!conflict} className="btn btn-primary w-full" data-testid="btn-book">
                    {submitting ? t("service_detail.sending_request", "Sending request…") : t("service_detail.request_to_book", "Request booking")}
                  </button>
                  {!isPublished && (
                    <div className="text-xs text-rose-200/80 text-center" data-testid="not-bookable-hint">
                      {t("service_detail.not_bookable_hint", "Bookings open once this service is published.")}
                    </div>
                  )}
                  {!user && <div className="text-xs text-muted text-center">{t("service_detail.sign_in_hint", "Sign in to request this booking.")}</div>}
                </form>
              ) : (
                <div className="luxe-card p-7 space-y-4" data-testid="booking-confirmation">
                  <div className="font-script text-3xl text-teal">{t("service_detail.request_sent_title", "Request sent.")}</div>
                  <p className="text-sm text-muted">
                    {t("service_detail.request_sent_body", "Your request is with the provider. We'll notify you the moment they confirm.")}
                  </p>
                  <div className="luxe-card p-4 text-sm space-y-1.5">
                    <div className="flex justify-between"><span className="text-muted">{t("service_detail.reference", "Reference")}</span><span className="font-mono text-xs">{bookingResult.id.slice(0,8)}</span></div>
                    <div className="flex justify-between"><span className="text-muted">{t("service_detail.status", "Status")}</span><span className="chip warning">{t("booking_status.requested", "Awaiting provider")}</span></div>
                    <div className="flex justify-between"><span className="text-muted">{t("service_detail.deposit", "Deposit")}</span><span>{format(bookingResult.deposit_required, bookingResult.currency)}</span></div>
                  </div>
                  <Link to="/dashboard" className="btn btn-outline w-full" data-testid="btn-go-dashboard">
                    {t("service_detail.view_dashboard", "View in dashboard")} →
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
